// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  Loader2,
  FolderOpen,
  Calendar,
  ChevronRight,
  ArrowLeft,
  FileText,
  ExternalLink,
  Download,
  MessageCircle,
} from 'lucide-react';

function FolderGrid({ items, icon: Icon, iconClass, onSelect, emptyMessage, cols }) {
  if (items.length === 0 && emptyMessage) {
    return (
      <p className="text-sm text-muted-foreground col-span-full text-center py-6">
        {emptyMessage}
      </p>
    );
  }
  return (
    <div className={`grid ${cols} gap-3`}>
      {items.map(item => (
        <Card
          key={item.id}
          className="cursor-pointer hover:shadow-md hover:border-primary/50 transition-all"
          onClick={() => onSelect(item)}
        >
          <CardContent className="flex flex-col items-center justify-center py-6 px-4">
            <Icon className={`w-8 h-8 mb-2 ${iconClass}`} />
            <span className="font-semibold text-foreground text-sm text-center">
              {item.name.replace(/_/g, ' ')}
            </span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function AuditFolderBrowser({
  loading,
  breadcrumb,
  selectedYear,
  selectedTrimester,
  selectedMonth,
  years,
  trimesters,
  months,
  subfolders,
  files,
  onBack,
  onSelectYear,
  onSelectTrimester,
  onSelectMonth,
  onOpenSubfolder,
  onOpenFile,
  onDownloadFile,
  onCloseMonth,
}) {
  return (
    <div>
      {breadcrumb.length > 0 && (
        <div className="flex items-center gap-1 mb-3 flex-wrap">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="folder-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
          </Button>
          <span className="text-sm text-muted-foreground mx-2">/</span>
          {breadcrumb.map((name, i) => (
            <span key={`bc-${name}-${i}`} className="flex items-center gap-1 text-sm">
              {i > 0 && <ChevronRight className="w-3 h-3 text-muted-foreground" />}
              <span
                className={
                  i === breadcrumb.length - 1
                    ? 'font-medium text-foreground'
                    : 'text-muted-foreground'
                }
              >
                {name}
              </span>
            </span>
          ))}
          {selectedMonth && (
            <Button
              variant="default"
              size="sm"
              onClick={onCloseMonth}
              className="ml-auto bg-green-600 hover:bg-green-700 text-white"
              data-testid="close-month-btn"
            >
              <MessageCircle className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Fechar Mês</span>
              <span className="sm:hidden">Fechar</span>
            </Button>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : !selectedYear ? (
        <FolderGrid
          items={years}
          icon={Calendar}
          iconClass="text-primary"
          onSelect={onSelectYear}
          emptyMessage="Nenhuma pasta de ano encontrada"
          cols="grid-cols-2 sm:grid-cols-3 md:grid-cols-4"
        />
      ) : !selectedTrimester ? (
        <FolderGrid
          items={trimesters}
          icon={FolderOpen}
          iconClass="text-amber-500"
          onSelect={onSelectTrimester}
          cols="grid-cols-2 sm:grid-cols-4"
        />
      ) : !selectedMonth ? (
        <FolderGrid
          items={months}
          icon={FolderOpen}
          iconClass="text-blue-500"
          onSelect={onSelectMonth}
          cols="grid-cols-2 sm:grid-cols-3"
        />
      ) : (
        <div className="space-y-2">
          {subfolders.length === 0 && files.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              Nenhum conteúdo nesta pasta
            </p>
          ) : (
            <>
              {subfolders.map(folder => (
                <Card
                  key={folder.id}
                  className="cursor-pointer hover:shadow-md hover:border-primary/50 transition-all"
                  onClick={() => onOpenSubfolder(folder)}
                >
                  <CardContent className="flex items-center gap-3 p-3">
                    <FolderOpen className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <span className="flex-1 text-sm font-medium text-foreground">
                      {folder.name.replace(/_/g, ' ')}
                    </span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </CardContent>
                </Card>
              ))}
              {files.map(file => (
                <Card key={file.id} className="hover:shadow-sm transition-shadow">
                  <CardContent className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3">
                    <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    <span className="flex-1 text-xs sm:text-sm font-medium text-foreground truncate">
                      {file.original_filename}
                    </span>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => onOpenFile(file)}
                      data-testid={`open-file-${file.id}`}
                      className="h-8 text-xs"
                    >
                      <ExternalLink className="w-3.5 h-3.5 mr-1" /> Abrir
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDownloadFile(file)}
                      className="h-8"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
